import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccess } from "../utils/permissions";
import "../styles/Footer.css";

const LeafIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

const Footer = () => {
  const { user } = useAuth();
  const year = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Define all possible quick links with their permission keys
  const allQuickLinks = [
    { label: "Capture", path: "/capture", feature: "capture" },
    { label: "Harvest Data", path: "/harvest-data-capture", feature: "harvest_data_capture" },
    { label: "Intake QC", path: "/intake-qc", feature: "intake_qc" },
    { label: "Rejections", path: "/rejections", feature: "rejections" },
    { label: "Session", path: "/session", feature: "session" },
    { label: "Manage", path: "/manage", feature: "manage" },
    { label: "Users", path: "/users", feature: "users" },
  ];

  // Filter links based on user permissions
  const quickLinks = allQuickLinks.filter((link) => canAccess(user, link.feature));

  return (
    <footer className="smart-footer">
      {/* Top Wave Decoration */}
      <div className="footer-wave">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 37 1344 30 1392 26.7L1440 23V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="var(--bg-primary)" />
        </svg>
      </div>

      {/* Main Footer Content */}
      <div className="footer-main">
        <div className="footer-inner">
          {/* Brand Column */}
          <div className="footer-brand-col">
            <div className="footer-brand-header">
              <div className="footer-brand-icon">
                <LeafIcon />
              </div>
              <div className="footer-brand-text">
                <span className="footer-brand-name">SmartHarvest</span>
                <span className="footer-brand-tagline">Quality First</span>
              </div>
            </div>
            <p className="footer-brand-desc">
              Optimizing efficiency and quality in every harvest. 
              Built for modern agriculture management.
            </p>
            <div className="footer-brand-accent">
              <span className="accent-dot" />
              <span>System Operational</span>
            </div>
          </div>

          {/* Quick Links — Permission-Based */}
          {quickLinks.length > 0 && (
            <div className="footer-links-col">
              <h4 className="footer-col-title">Quick Links</h4>
              <ul className="footer-links">
                {quickLinks.map((link) => (
                  <li key={link.feature}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <div className="footer-bottom-inner">
          <p className="footer-copyright">
            &copy; {year} <span className="footer-brand-highlight">SmartHarvest</span>. 
            Crafted with <span className="footer-heart"><HeartIcon /></span> for agriculture.
          </p>
          
          <div className="footer-bottom-links">
            <a href="#">Privacy</a>
            <span className="footer-divider" />
            <a href="#">Terms</a>
            <span className="footer-divider" />
            <a href="#">Cookies</a>
          </div>

          <button 
            className="scroll-top-btn" 
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <ArrowUpIcon />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;